// GroupManagement.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Link,
  FormControl, InputLabel, Select, MenuItem, Tooltip, TablePagination, FormControlLabel, Switch, Chip, FormHelperText,
  Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
// import { formatCurrency } from "../shared/formatCurrency"; // Assuming you have this utility
// import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";
import formatCurrency from "../shared/formatCurrency";
import { motion } from "framer-motion";
import {
  fetchGroups,
  createGroup,
  deleteGroup,
  updateGroup,
  fetchGroupTermStatus,
  closeGroupTerm,
  renewGroupTerm,
  fetchGroupNmbCredential,
  saveGroupNmbCredential,
  rotateGroupNmbPassword,
  verifyGroupNmbCredential,
  activateGroupNmbCredential,
  deactivateGroupNmbCredential,
} from "../services/groupService";
import LoadingSpinner from "../components/LoadingSpinner";
import LocationSelector from "../components/LocationSelector";
import {
  GROUP_CATEGORY_NZENGO,
  getGroupCategory,
  isNzengoGroup,
  withResolvedGroupCategory,
  writeStoredGroupCategory,
} from "../utils/groupCapabilities";

const brand = "#800b33";
const brandHover = "#6c0a2b";
const mergeMockCategories = (groups = []) => groups.map(withResolvedGroupCategory);

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.4 },
  }),
};

const GroupManagement = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [formErrors, setFormErrors] = useState({});
  const [editingGroup, setEditingGroup] = useState(null);
  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [termLoading, setTermLoading] = useState(false);
  const [termStatus, setTermStatus] = useState(null);
  const [termError, setTermError] = useState("");
  const [termGroup, setTermGroup] = useState(null);
  const [termForm, setTermForm] = useState({ start_date: "", end_date: "" });
  const [nmbDialogOpen, setNmbDialogOpen] = useState(false);
  const [nmbGroup, setNmbGroup] = useState(null);
  const [nmbStatus, setNmbStatus] = useState(null);
  const [nmbLoading, setNmbLoading] = useState(false);
  const [nmbForm, setNmbForm] = useState({ username: "", password: "", reference_prefix: "", rotate_password: "" });
  const [nmbErrors, setNmbErrors] = useState({});
  const [nmbMessage, setNmbMessage] = useState(null);

  const [form, setForm] = useState({
    name: "",
    cycle_type: "PERMANENT",
    frequency: "Weekly", // Default value
    contribution_amount: 10000, // Default value, matches backend default
    contribution_handling_mode: "PAYG_NO_PREPAY",
    chairperson: { name: "", email: "", phone: "" },
    nmb_account_number: "",
    mkoa: "",
    wilaya: "",
    entry_fee_amount: 0,
    upatu_enabled: false,
    start_date: "",
    end_date: "",
    group_category: "NORMAL",
  });
  const normalizedAccountDigits = (form.nmb_account_number || "").replace(/\D/g, "");
  const showAccountNumberError = !!form.nmb_account_number && normalizedAccountDigits.length !== 11;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const groupsData = await fetchGroups();
      setGroups(mergeMockCategories(groupsData));
      setError(null);
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: "error", title: t("Failed to load groups"), text: err.message, confirmButtonColor: brand });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [query, groups]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return filtered;
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    const val = parseInt(e.target.value, 10);
    setRowsPerPage(val);
    setPage(0);
  };

  const handleOpenAdd = () => {
    setDialogMode("add");
    setFormErrors({});
    setForm({
      name: "",
      cycle_type: "PERMANENT",
      frequency: "Weekly",
      contribution_amount: 10000,
      contribution_handling_mode: "PAYG_NO_PREPAY",
      chairperson: { name: "", email: "", phone: "" },
      nmb_account_number: "",
      mkoa: "",
      wilaya: "",
      entry_fee_amount: 0,
      upatu_enabled: false,
      start_date: "",
      end_date: "",
      group_category: "NORMAL",
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (group) => {
    setDialogMode("edit");
    setEditingGroup(group);
    setFormErrors({});
    setForm({
      name: group.name,
      cycle_type: group.cycle_type || "PERMANENT",
      frequency: group.frequency,
      contribution_amount: group.contribution_amount,
      contribution_handling_mode: group.contribution_handling_mode || "PAYG_NO_PREPAY",
      // Chairperson info is not editable in this dialog
      chairperson: { name: "", email: "", phone: "" },
      nmb_account_number: group.nmb_account_number || "",
      mkoa: group.mkoa || "",
      wilaya: group.wilaya || "",
      entry_fee_amount: group.entry_fee_amount || 0,
      upatu_enabled: Boolean(group.upatu_enabled),
      start_date: group.start_date || "",
      end_date: group.end_date || "",
      group_category: getGroupCategory(group),
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
    setEditingGroup(null);
  };

  const handleOpenTermStatus = async (group) => {
    setTermGroup(group);
    setTermError("");
    setTermStatus(null);
    setTermForm({ start_date: "", end_date: "" });
    setTermDialogOpen(true);
    setTermLoading(true);
    try {
      const data = await fetchGroupTermStatus(group.id);
      setTermStatus(data);
      if (data?.last_term?.end_date) {
        const nextStart = new Date(data.last_term.end_date);
        nextStart.setDate(nextStart.getDate() + 1);
        const startValue = nextStart.toISOString().slice(0, 10);
        setTermForm((prev) => ({ ...prev, start_date: startValue }));
      }
    } catch (err) {
      setTermError(err.message || t("Failed to load term status."));
    } finally {
      setTermLoading(false);
    }
  };

  const handleCloseTermDialog = () => {
    setTermDialogOpen(false);
    setTermStatus(null);
    setTermGroup(null);
    setTermError("");
    setTermForm({ start_date: "", end_date: "" });
  };

  const handleConfirmCloseTerm = async () => {
    if (!termGroup) return;
    setTermLoading(true);
    try {
      await closeGroupTerm(termGroup.id);
      Swal.fire({ icon: "success", title: t("Term Closed"), confirmButtonColor: brand });
      const data = await fetchGroupTermStatus(termGroup.id);
      setTermStatus(data);
      loadData();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Close Failed"), text: err.message, confirmButtonColor: brand });
    } finally {
      setTermLoading(false);
    }
  };

  const handleConfirmRenewTerm = async () => {
    if (!termGroup) return;
    if (!termForm.start_date || !termForm.end_date) {
      Swal.fire({ icon: "warning", title: t("Missing Dates"), text: t("Start and end dates are required."), confirmButtonColor: brand });
      return;
    }
    setTermLoading(true);
    try {
      await renewGroupTerm(termGroup.id, {
        start_date: termForm.start_date,
        end_date: termForm.end_date,
      });
      Swal.fire({ icon: "success", title: t("Term Renewed"), confirmButtonColor: brand });
      const data = await fetchGroupTermStatus(termGroup.id);
      setTermStatus(data);
      loadData();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Renew Failed"), text: err.message, confirmButtonColor: brand });
    } finally {
      setTermLoading(false);
    }
  };

  const handleSave = async () => {
    const sanitizeDate = (value) => (value ? String(value).trim() : "");
    const startDate = sanitizeDate(form.start_date);
    const endDate = form.cycle_type === "TEMPORARY" ? sanitizeDate(form.end_date) : "";
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t("Group name is required.");
    if (!form.cycle_type) nextErrors.cycle_type = t("Group type is required.");
    if (!startDate) nextErrors.start_date = t("Start date is required.");
    if (form.cycle_type === "TEMPORARY") {
      if (!endDate) {
        nextErrors.end_date = t("End date is required for temporary groups.");
      } else if (startDate && new Date(endDate) <= new Date(startDate)) {
        nextErrors.end_date = t("End date must be after the start date.");
      }
    }
    if (dialogMode === 'add' && !form.chairperson.name.trim()) nextErrors.chairperson_name = t("Chairperson name is required.");
    if (dialogMode === 'add' && !form.chairperson.phone.trim()) nextErrors.chairperson_phone = t("Chairperson phone is required.");
    if (!form.mkoa.trim()) nextErrors.mkoa = t("Mkoa (Region) is required.");
    if (!form.wilaya.trim()) nextErrors.wilaya = t("Wilaya (District) is required.");
    if (form.entry_fee_amount < 0) nextErrors.entry_fee_amount = t("Entry fee cannot be negative.");

    const normalizedAccount = normalizedAccountDigits;
    if (!/^\d{11}$/.test(normalizedAccount)) {
      nextErrors.nmb_account_number = t("NMB account must be exactly 11 digits.");
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = {
      ...form,
      nmb_account_number: normalizedAccount,
      start_date: startDate,
      end_date: form.cycle_type === "TEMPORARY" ? endDate || null : null,
    };
    delete payload.group_category;

    if (dialogMode === "add") {
      try {
        await createGroup(payload);
        writeStoredGroupCategory({ name: form.name }, form.group_category);
        Swal.fire({ icon: "success", title: t("Group Created Successfully!"), text: t("The chairperson will need to activate their account on first login."), confirmButtonColor: brand });
      } catch (err) {
        // Custom error handling for unique phone number constraint
        if (err.message && err.message.includes("UNIQUE constraint failed: profiles_customuser.phone_number")) {
          setFormErrors(prev => ({ ...prev, chairperson_phone: t("This phone number is already registered to another user.") }));
          Swal.fire({
            icon: "error", title: t("Creation Failed"), text: t("A user with this phone number already exists. Please use a different number for the chairperson."), confirmButtonColor: brand
          });
        } else {
          Swal.fire({ icon: "error", title: t("Creation Failed"), text: err.message, confirmButtonColor: brand });
        }
      }
    } else { // Handle Edit
      const payloadUpdate = {
        name: form.name,
        cycle_type: form.cycle_type,
        frequency: form.frequency,
        contribution_amount: form.contribution_amount,
        contribution_handling_mode: form.contribution_handling_mode,
        nmb_account_number: normalizedAccount,
        mkoa: form.mkoa,
        wilaya: form.wilaya,
        upatu_enabled: form.upatu_enabled,
        start_date: startDate,
        end_date: form.cycle_type === "TEMPORARY" ? endDate || null : null,
      };
      try {
        await updateGroup(editingGroup.id, payloadUpdate);
        writeStoredGroupCategory(editingGroup, form.group_category);
        Swal.fire({ icon: "success", title: t("Group Updated!"), confirmButtonColor: brand });
      } catch (err) {
        Swal.fire({ icon: "error", title: t("Update Failed"), text: err.message, confirmButtonColor: brand });
      }
    }
    loadData();
    handleCloseDialog();
  };

  const handleDelete = async (group) => {
    Swal.fire({
      title: t("Delete Group?"),
      text: t("Are you sure you want to remove {{name}}?", { name: group.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand,
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("Delete"),
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteGroup(group.id);
          Swal.fire({ icon: "success", title: t("Deleted!"), text: t("{{name}} has been removed.", { name: group.name }), confirmButtonColor: brand });
          loadData(); // Refresh data
        } catch (err) {
          Swal.fire({ icon: "error", title: t("Deletion Failed"), text: err.message, confirmButtonColor: brand });
        }
      }
    });
  };

  const handleOpenNmbConfig = async (group) => {
    setNmbGroup(group);
    setNmbStatus(group.nmb_credential || null);
    setNmbErrors({});
    setNmbMessage(null);
    setNmbForm({
      username: "",
      password: "",
      reference_prefix: group.nmb_credential?.reference_prefix || "",
      rotate_password: "",
    });
    setNmbDialogOpen(true);
    setNmbLoading(true);
    try {
      const status = await fetchGroupNmbCredential(group.id);
      setNmbStatus(status);
      setNmbForm((prev) => ({ ...prev, reference_prefix: status.reference_prefix || "" }));
    } catch (err) {
      setNmbMessage({ severity: "error", text: err.message || t("Failed to load NMB configuration") });
    } finally {
      setNmbLoading(false);
    }
  };

  const formatNmbVerificationMessage = (verification) => {
    if (!verification?.description) {
      return t("NMB credentials were saved, but verification did not succeed.");
    }
    const details = [];
    if (verification?.nmb_error?.http_status) details.push(`HTTP ${verification.nmb_error.http_status}`);
    if (verification?.nmb_error?.error_type) details.push(verification.nmb_error.error_type);
    if (verification?.nmb_error?.endpoint) details.push(verification.nmb_error.endpoint);
    return details.length ? `${verification.description} (${details.join(" - ")})` : verification.description;
  };

  const handleSaveNmbConfig = async () => {
    if (!nmbGroup) return;
    const exists = Boolean(nmbStatus?.has_password || nmbStatus?.masked_username || nmbStatus?.reference_prefix);
    const payload = {};
    const nextErrors = {};
    if (nmbForm.username.trim()) payload.username = nmbForm.username.trim();
    if (nmbForm.reference_prefix.trim()) payload.reference_prefix = nmbForm.reference_prefix.trim();
    if (!exists && nmbForm.password) payload.password = nmbForm.password;

    if (!exists && !payload.username) {
      nextErrors.username = t("Username is required when creating NMB credentials.");
    }
    if (!exists && !payload.reference_prefix) {
      nextErrors.reference_prefix = t("Reference prefix is required when creating NMB credentials.");
    }
    if (!exists && !payload.password) {
      nextErrors.password = t("Password is required when creating NMB credentials.");
    }
    if (Object.keys(nextErrors).length > 0) {
      setNmbErrors(nextErrors);
      setNmbMessage({ severity: "warning", text: t("Complete the highlighted NMB credential fields.") });
      return;
    }
    setNmbErrors({});
    setNmbMessage(null);
    setNmbLoading(true);
    try {
      const status = await saveGroupNmbCredential(nmbGroup.id, payload, exists);
      setNmbStatus(status);
      setNmbForm((prev) => ({ ...prev, username: "", password: "", reference_prefix: status.reference_prefix || prev.reference_prefix }));
      const verified = status?.verification?.status === "1" || status?.is_verified;
      setNmbMessage({
        severity: verified ? "success" : "warning",
        text: verified
          ? t("NMB returned a valid token for this group.")
          : formatNmbVerificationMessage(status?.verification),
      });
      loadData();
    } catch (err) {
      setNmbMessage({ severity: "error", text: err.message || t("Unable to save NMB configuration.") });
    } finally {
      setNmbLoading(false);
    }
  };

  const handleRotateNmbPassword = async () => {
    if (!nmbGroup) return;
    if (!nmbForm.rotate_password) {
      setNmbErrors({ rotate_password: t("New password is required.") });
      setNmbMessage({ severity: "warning", text: t("Enter the new NMB password before rotating.") });
      return;
    }
    setNmbErrors({});
    setNmbMessage(null);
    setNmbLoading(true);
    try {
      const status = await rotateGroupNmbPassword(nmbGroup.id, nmbForm.rotate_password);
      setNmbStatus(status);
      setNmbForm((prev) => ({ ...prev, rotate_password: "" }));
      setNmbMessage({ severity: "success", text: t("NMB returned a valid token using the new password.") });
      loadData();
    } catch (err) {
      setNmbErrors({ rotate_password: t("The new password could not be verified.") });
      setNmbMessage({ severity: "error", text: err.message || t("Password rotation failed.") });
    } finally {
      setNmbLoading(false);
    }
  };

  const handleVerifyNmbCredential = async () => {
    if (!nmbGroup) return;
    setNmbErrors({});
    setNmbMessage(null);
    setNmbLoading(true);
    try {
      await verifyGroupNmbCredential(nmbGroup.id);
      const status = await fetchGroupNmbCredential(nmbGroup.id);
      setNmbStatus(status);
      setNmbMessage({ severity: "success", text: t("NMB returned a valid token for this group.") });
      loadData();
    } catch (err) {
      setNmbMessage({ severity: "error", text: err.message || t("Verification failed.") });
    } finally {
      setNmbLoading(false);
    }
  };

  const handleToggleNmbCredential = async () => {
    if (!nmbGroup || !nmbStatus) return;
    setNmbLoading(true);
    try {
      const status = nmbStatus.is_active
        ? await deactivateGroupNmbCredential(nmbGroup.id)
        : await activateGroupNmbCredential(nmbGroup.id);
      setNmbStatus(status);
      setNmbMessage({
        severity: "success",
        text: nmbStatus.is_active ? t("NMB credentials deactivated.") : t("NMB credentials activated."),
      });
      loadData();
    } catch (err) {
      setNmbMessage({ severity: "error", text: err.message || t("Action failed.") });
    } finally {
      setNmbLoading(false);
    }
  };

  const resolveGroupType = (group) => {
    const formatFromValue = (value) => {
      if (value === null || value === undefined) return null;
      const raw = String(value).trim();
      if (!raw) return null;
      const normalized = raw.toLowerCase();
      if (normalized.includes("temp")) {
        return { label: t("Temporary"), tone: "warning" };
      }
      if (normalized.includes("perm")) {
        return { label: t("Permanent"), tone: "success" };
      }
      return { label: raw, tone: "default" };
    };

    const fromExplicitValue =
      formatFromValue(group.group_type) ||
      formatFromValue(group.type) ||
      formatFromValue(group.groupType) ||
      formatFromValue(group.cycle_type);

    if (fromExplicitValue) return fromExplicitValue;
    if (typeof group.is_temporary === "boolean") {
      return group.is_temporary
        ? { label: t("Temporary"), tone: "warning" }
        : { label: t("Permanent"), tone: "success" };
    }
    if (typeof group.temporary === "boolean") {
      return group.temporary
        ? { label: t("Temporary"), tone: "warning" }
        : { label: t("Permanent"), tone: "success" };
    }
    if (typeof group.is_permanent === "boolean") {
      return group.is_permanent
        ? { label: t("Permanent"), tone: "success" }
        : { label: t("Temporary"), tone: "warning" };
    }
    return { label: t("Unknown"), tone: "default" };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 900, color: brand }}>
        {t("Group Management")}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        {t("Register new Savvy Community groups and set their initial Chairperson.")}
      </Typography>

      {/* Toolbar */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t("Search group by name...")}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                {t("Register New Group")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Typography color="error" align="center" sx={{ my: 4 }}>
          {t("Failed to load dashboard: {{error}}", { error })}
        </Typography>
      )}

      {/* Table */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Group Name")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Category")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Group Type")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Chairperson")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Members")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Frequency")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Contribution Amount")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("NMB Account")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("NMB Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Mkoa")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Wilaya")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Upatu?")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 && !loading ? (
                    <TableRow >
                      <TableCell colSpan={14} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        {t("No groups found.")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((g, idx) => {
                      const groupType = resolveGroupType(g);
                      const isNzengo = isNzengoGroup(g);
                      return (
                        <TableRow key={g.id} hover>
                          <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                          <TableCell>{g.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={isNzengo ? t("Local Community Unit (Nzengo)") : t("Existing/Normal Group")}
                              color={isNzengo ? "secondary" : "default"}
                              size="small"
                              variant={isNzengo ? "filled" : "outlined"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={groupType.label}
                              color={groupType.tone}
                              size="small"
                              variant={groupType.tone === "default" ? "outlined" : "filled"}
                            />
                          </TableCell>
                          <TableCell>{g.chairperson?.full_name}: {g.chairperson?.phone_number}</TableCell>
                          <TableCell>{g.members_count || 0}</TableCell>
                          <TableCell>{g.frequency}</TableCell>
                          <TableCell>{formatCurrency(g.contribution_amount, locale)} </TableCell>
                          <TableCell>{g.nmb_account_number || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={g.nmb_credential?.onboarding_status || "NOT_CONFIGURED"}
                              color={g.nmb_credential?.is_verified ? "success" : "warning"}
                              variant={g.nmb_credential?.is_verified ? "filled" : "outlined"}
                            />
                          </TableCell>
                          <TableCell>{g.mkoa || "—"}</TableCell>
                          <TableCell>{g.wilaya || "—"}</TableCell>
                          <TableCell>{g.upatu_enabled ? t("Yes") : t("No")}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t("NMB Configuration")}>
                            <IconButton onClick={() => handleOpenNmbConfig(g)} size="small" sx={{ mr: 0.5 }}>
                              <VpnKeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Term Status")}>
                            <IconButton onClick={() => handleOpenTermStatus(g)} size="small" sx={{ mr: 0.5 }}>
                              <EventNoteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Edit Group")}>
                            <IconButton onClick={() => handleOpenEdit(g)} size="small" sx={{ mr: 0.5 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                            <Tooltip title={t("Delete")}>
                              <IconButton onClick={() => handleDelete(g)} color="error" size="small">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage === -1 ? filtered.length : rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100, { label: t("All"), value: -1 }]}
              />
            </Box>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {dialogMode === 'add' ? t('Register New Group') : t('Edit Group')}
          <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: brand }}>
            {t("Group Information")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("Group Name")}
                fullWidth
                value={form.name}
                error={!!formErrors.name}
                helperText={formErrors.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Group Category")}</InputLabel>
                <Select
                  label={t("Group Category")}
                  value={form.group_category}
                  onChange={(e) => setForm((f) => ({ ...f, group_category: e.target.value }))}
                >
                  <MenuItem value="NORMAL">{t("Existing/Normal Group")}</MenuItem>
                  <MenuItem value={GROUP_CATEGORY_NZENGO}>{t("Local Community Unit (Nzengo)")}</MenuItem>
                </Select>
                <FormHelperText>
                  {form.group_category === GROUP_CATEGORY_NZENGO
                    ? t("Nzengo is contribution-based and does not support loan workflows.")
                    : t("Use this for the current standard Savvy Community group experience.")}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.cycle_type}>
                <InputLabel>{t("Group Type")}</InputLabel>
                <Select
                  label={t("Group Type")}
                  value={form.cycle_type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setForm((f) => ({
                      ...f,
                      cycle_type: nextType,
                      end_date: nextType === "TEMPORARY" ? f.end_date : "",
                    }));
                  }}
                >
                  <MenuItem value="PERMANENT">{t("Permanent")}</MenuItem>
                  <MenuItem value="TEMPORARY">{t("Temporary")}</MenuItem>
                </Select>
                <FormHelperText>
                  {formErrors.cycle_type || t("Permanent groups are ongoing; temporary groups are time-bound.")}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Meeting Frequency")}</InputLabel>
                <Select
                  label={t("Meeting Frequency")}
                  value={form.frequency}
                  onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
                >
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Contribution Handling Mode")}</InputLabel>
                <Select
                  label={t("Contribution Handling Mode")}
                  value={form.contribution_handling_mode}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contribution_handling_mode: e.target.value }))
                  }
                >
                  <MenuItem value="PAYG_NO_PREPAY">{t("Pay-as-you-go (No Prepay)")}</MenuItem>
                  <MenuItem value="PREPAY_ADVANCE_ALLOWED">{t("Prepaid (Advance Allowed)")}</MenuItem>
                </Select>
                <FormHelperText>
                  {form.contribution_handling_mode === "PREPAY_ADVANCE_ALLOWED"
                    ? t("Extra deposits can cover future periods.")
                    : t("Extra deposits stay as savings and do not move due dates.")}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={form.cycle_type === "TEMPORARY" ? 6 : 12}>
              <TextField
                label={t("Start Date")}
                type="date"
                fullWidth
                value={form.start_date}
                error={!!formErrors.start_date}
                helperText={formErrors.start_date || t("Required for auditability and term tracking.")}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {form.cycle_type === "TEMPORARY" && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("End Date")}
                  type="date"
                  fullWidth
                  value={form.end_date}
                  error={!!formErrors.end_date}
                  helperText={formErrors.end_date || t("Required for temporary groups.")}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  inputProps={{ min: form.start_date || undefined }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}> {/* Changed from Select to TextField */}
              <FormControl fullWidth>
                <TextField
                  label={t("Contribution Amount (TZS)")}
                  type="number"
                  fullWidth
                  value={form.contribution_amount}
                  onChange={(e) => setForm((f) => ({ ...f, contribution_amount: Number(e.target.value) }))}
                  inputProps={{ min: 0 }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Entry Fee Amount (TZS)")}
                type="number"
                fullWidth
                value={form.entry_fee_amount}
                onChange={(e) => setForm((f) => ({ ...f, entry_fee_amount: Number(e.target.value) }))}
                inputProps={{ min: 0 }}
                helperText={t("Required upfront from chairperson and new members. Set 0 to disable.")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("Group NMB Account Number")}
                required
                fullWidth
                value={form.nmb_account_number}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setForm((f) => ({ ...f, nmb_account_number: digits }));
                }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 11 }}
                error={showAccountNumberError}
                helperText={
                  showAccountNumberError
                    ? t("Enter the 11-digit NMB business account number.")
                    : t("Required: 11-digit NMB account for automated settlements.")
                }
              />
            </Grid>
            <Grid item xs={12}>
              <LocationSelector
                region={form.mkoa}
                district={form.wilaya}
                errors={{ region: formErrors.mkoa, district: formErrors.wilaya }}
                onChange={({ region, district }) =>
                  setForm((f) => ({ ...f, mkoa: region, wilaya: district }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.upatu_enabled}
                    onChange={(e) => setForm((f) => ({ ...f, upatu_enabled: e.target.checked }))}
                    color="primary"
                  />
                }
                label={t("Enable Upatu (chama/rotational savings mode)")}
              />
            </Grid>
          </Grid>

          {dialogMode === 'add' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: brand }}>
                {t("Initial Chairperson Info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("Full Name")}
                    required
                    fullWidth
                    value={form.chairperson.name}
                    error={!!formErrors.chairperson_name}
                    helperText={formErrors.chairperson_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, chairperson: { ...f.chairperson, name: e.target.value } }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PhoneInput
                    label={t("Phone Number")}
                    required
                    fullWidth
                    value={form.chairperson.phone}
                    error={!!formErrors.chairperson_phone}
                    helperText={formErrors.chairperson_phone}
                    onChange={(val) =>
                      setForm((f) => ({ ...f, chairperson: { ...f.chairperson, phone: val } }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Email (Optional)")}
                    type="email"
                    fullWidth
                    value={form.chairperson.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, chairperson: { ...f.chairperson, email: e.target.value } }))
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nmbDialogOpen} onClose={() => setNmbDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {t("NMB Configuration")}
          <IconButton onClick={() => setNmbDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {nmbLoading && <LoadingSpinner />}
          {nmbMessage && (
            <Alert
              severity={nmbMessage.severity || "info"}
              sx={{ mb: 2, position: "sticky", top: 0, zIndex: 2 }}
            >
              {nmbMessage.text}
            </Alert>
          )}
          {nmbGroup && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Alert severity={nmbStatus?.is_verified ? "success" : "warning"}>
                {nmbStatus?.is_verified
                  ? t("NMB credentials are verified for this group.")
                  : t("NMB credentials are not yet configured or verified for this group.")}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Group")}</Typography>
                  <Typography variant="body2" fontWeight={700}>{nmbGroup.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("NMB Account")}</Typography>
                  <Typography variant="body2" fontFamily="monospace">{nmbStatus?.nmb_account_number || nmbGroup.nmb_account_number || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Status")}</Typography>
                  <Box>
                    <Chip
                      size="small"
                      label={nmbStatus?.onboarding_status || "NOT_CONFIGURED"}
                      color={nmbStatus?.is_verified ? "success" : "warning"}
                      variant={nmbStatus?.is_verified ? "filled" : "outlined"}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Username")}</Typography>
                  <Typography variant="body2">{nmbStatus?.masked_username || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Password Configured")}</Typography>
                  <Typography variant="body2">{nmbStatus?.has_password ? t("Yes") : t("No")}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Reference Prefix")}</Typography>
                  <Typography variant="body2" fontFamily="monospace">{nmbStatus?.reference_prefix || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Last Verified")}</Typography>
                  <Typography variant="body2">{nmbStatus?.verified_at ? new Date(nmbStatus.verified_at).toLocaleString() : "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t("Last Updated")}</Typography>
                  <Typography variant="body2">{nmbStatus?.updated_at ? new Date(nmbStatus.updated_at).toLocaleString() : "—"}</Typography>
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand }}>
                {t("Add or Update Credentials")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
	                  <TextField
	                    label={t("Username")}
	                    fullWidth
	                    value={nmbForm.username}
	                    onChange={(e) => {
                        setNmbForm((f) => ({ ...f, username: e.target.value }));
                        setNmbErrors((prev) => ({ ...prev, username: "" }));
                      }}
                      error={!!nmbErrors.username}
	                    helperText={nmbErrors.username || t("Leave blank to keep existing username.")}
	                  />
	                </Grid>
	                <Grid item xs={12} md={6}>
	                  <TextField
	                    label={t("Reference Prefix")}
	                    fullWidth
	                    value={nmbForm.reference_prefix}
	                    onChange={(e) => {
                        setNmbForm((f) => ({ ...f, reference_prefix: e.target.value.toUpperCase() }));
                        setNmbErrors((prev) => ({ ...prev, reference_prefix: "" }));
                      }}
                      error={!!nmbErrors.reference_prefix}
                      helperText={nmbErrors.reference_prefix || t("Uppercase letters and numbers only.")}
	                  />
	                </Grid>
                {!nmbStatus?.has_password && (
                  <Grid item xs={12}>
                    <TextField
                      label={t("Password")}
	                      type="password"
	                      fullWidth
	                      value={nmbForm.password}
	                      onChange={(e) => {
                          setNmbForm((f) => ({ ...f, password: e.target.value }));
                          setNmbErrors((prev) => ({ ...prev, password: "" }));
                        }}
                        error={!!nmbErrors.password}
	                      helperText={nmbErrors.password || t("Password is write-only and will never be displayed again.")}
	                    />
	                  </Grid>
                )}
              </Grid>

              <Button
                variant="contained"
                onClick={handleSaveNmbConfig}
                disabled={nmbLoading}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover }, justifySelf: "start" }}
              >
                {t("Save NMB Configuration")}
              </Button>

              {nmbStatus?.has_password && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand }}>
                    {t("Rotate Password")}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <TextField
                        label={t("New Password")}
	                        type="password"
	                        fullWidth
	                        value={nmbForm.rotate_password}
	                        onChange={(e) => {
                            setNmbForm((f) => ({ ...f, rotate_password: e.target.value }));
                            setNmbErrors((prev) => ({ ...prev, rotate_password: "" }));
                          }}
                          error={!!nmbErrors.rotate_password}
                          helperText={nmbErrors.rotate_password || t("The new password will be verified with NMB before it replaces the current one.")}
	                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="warning"
                        onClick={handleRotateNmbPassword}
                        disabled={nmbLoading || !nmbForm.rotate_password}
                      >
                        {t("Rotate")}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNmbDialogOpen(false)}>{t("Close")}</Button>
          <Button onClick={handleVerifyNmbCredential} disabled={nmbLoading || !nmbStatus?.has_password}>
            {t("Verify")}
          </Button>
          <Button
            variant="outlined"
            color={nmbStatus?.is_active ? "warning" : "success"}
            onClick={handleToggleNmbCredential}
            disabled={nmbLoading || !nmbStatus?.is_verified}
          >
            {nmbStatus?.is_active ? t("Deactivate") : t("Activate")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={termDialogOpen} onClose={handleCloseTermDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {t("Term Close & Renewal")}
          <IconButton onClick={handleCloseTermDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {termLoading && <LoadingSpinner />}
          {termError && <Alert severity="error" sx={{ mb: 2 }}>{termError}</Alert>}
          {termStatus && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: brand }}>
                {t("Current Term")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {termStatus.current_term
                  ? t("Term {{number}} - {{status}} - {{start}} to {{end}}", {
                    number: termStatus.current_term.term_number,
                    status: termStatus.current_term.status,
                    start: termStatus.current_term.start_date || "---",
                    end: termStatus.current_term.end_date || "---",
                  })
                  : t("No active term.")}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: brand }}>
                {t("Pre-close Checklist")}
              </Typography>
              <Box sx={{ display: "grid", gap: 1 }}>
                {(termStatus.close_checklist || []).map((item) => (
                  <Box
                    key={item.key}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Typography variant="body2">
                      {item.label} ({item.count})
                    </Typography>
                    <Chip
                      label={item.ok ? t("OK") : t("Block")}
                      color={item.ok ? "success" : "warning"}
                      size="small"
                      variant={item.ok ? "filled" : "outlined"}
                    />
                  </Box>
                ))}
              </Box>
              {termStatus.close_blockers?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {termStatus.close_blockers.join(" | ")}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: brand }}>
                {t("Renew Term")}
              </Typography>
              {termStatus.cycle_type !== "TEMPORARY" ? (
                <Alert severity="info">{t("Permanent groups do not renew terms.")}</Alert>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("Start Date")}
                      type="date"
                      fullWidth
                      value={termForm.start_date}
                      onChange={(e) => setTermForm((f) => ({ ...f, start_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("End Date")}
                      type="date"
                      fullWidth
                      value={termForm.end_date}
                      onChange={(e) => setTermForm((f) => ({ ...f, end_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: termForm.start_date || undefined }}
                    />
                  </Grid>
                </Grid>
              )}
              {termStatus.renew_blockers?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {termStatus.renew_blockers.join(" | ")}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTermDialog}>{t("Close")}</Button>
          <Button
            variant="contained"
            onClick={handleConfirmCloseTerm}
            disabled={!termStatus?.can_close || termLoading}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Close Term")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmRenewTerm}
            disabled={!termStatus?.can_renew || termLoading}
          >
            {t("Renew Term")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupManagement;
